import api from './api';
import type {
  CustomerJourney,
  CustomerJourneyDetailResponse,
  CustomerJourneyListResponse,
  CustomerJourneyStatsResponse,
  JourneyDetailResponse,
  JourneyListResponse,
  JourneyStatsResponse,
  TechnicianJourney,
  TechnicianJourneyPhases,
} from '../types/technicianJourney';

export const DEFAULT_TECH_PHASES: TechnicianJourneyPhases = {
  signup: { completed: false, status: '', otpVerified: false },
  profileCompletion: {
    completed: false,
    profileComplete: false,
    hasEmail: false,
    hasDescription: false,
    hasCertificates: false,
    hasRegions: false,
    hasYearsOfExperience: false,
    hasProfileImage: false,
    hasNationalId: false,
  },
  adminApproval: { completed: false, pendingDataRequests: 0, dataRequests: [] },
  onboarding: {
    completed: false,
    services: { added: false, count: 0, list: [] },
    availability: { set: false, type: null, slotsCount: 0, slots: [] },
    subscription: {
      active: false,
      categoryNameAr: null,
      categoryNameEn: null,
      startDate: null,
      endDate: null,
    },
    smallTaskTypes: { subscribed: false, count: 0, list: [] },
  },
  portfolio: { created: false, public: false, pastProjectsCount: 0 },
};

export function mergeTechnicianPhases(p?: Partial<TechnicianJourneyPhases> | null): TechnicianJourneyPhases {
  if (!p) return { ...DEFAULT_TECH_PHASES, signup: { ...DEFAULT_TECH_PHASES.signup } };
  return {
    signup: { ...DEFAULT_TECH_PHASES.signup, ...p.signup },
    profileCompletion: { ...DEFAULT_TECH_PHASES.profileCompletion, ...p.profileCompletion },
    adminApproval: {
      ...DEFAULT_TECH_PHASES.adminApproval,
      ...p.adminApproval,
      dataRequests: p.adminApproval?.dataRequests ?? DEFAULT_TECH_PHASES.adminApproval.dataRequests,
    },
    onboarding: {
      ...DEFAULT_TECH_PHASES.onboarding,
      ...p.onboarding,
      services: { ...DEFAULT_TECH_PHASES.onboarding.services, ...p.onboarding?.services },
      availability: { ...DEFAULT_TECH_PHASES.onboarding.availability, ...p.onboarding?.availability },
      subscription: { ...DEFAULT_TECH_PHASES.onboarding.subscription, ...p.onboarding?.subscription },
      smallTaskTypes: { ...DEFAULT_TECH_PHASES.onboarding.smallTaskTypes, ...p.onboarding?.smallTaskTypes },
    },
    portfolio: { ...DEFAULT_TECH_PHASES.portfolio, ...p.portfolio },
  };
}

export function normalizeTechnicianJourney(j: TechnicianJourney): TechnicianJourney {
  return {
    ...j,
    phases: mergeTechnicianPhases(j.phases),
  };
}

export const MOCK_TECH_JOURNEY: TechnicianJourney = normalizeTechnicianJourney({
  technicianId: 42,
  name: 'Ahmed Technician (demo)',
  phoneNumber: '+966500000000',
  email: 'tech@example.com',
  registrationDate: '2026-03-01T12:00:00',
  acceptedTermsAt: '2026-03-01T12:00:00',
  city: 'Riyadh',
  status: 'APPROVED',
  currentPhase: 'ONBOARDING',
  overallProgress: 50,
  isOnline: true,
  lastSeen: new Date().toISOString(),
  activity: {
    lastLoginDate: '2026-04-01T16:40:00',
    loginCountLast30Days: 12,
    daysSinceLastActivity: 0,
  },
  quotes: {
    totalQuotes: 55,
    quotesLast7Days: 3,
    quotesLast30Days: 12,
    lastQuoteDate: '2026-04-01T10:30:00',
    quoteFrequency: 'MEDIUM',
  },
  performance: {
    wonProjects: 8,
    completedProjects: 5,
    inProgressProjects: 2,
    cancelledProjects: 1,
    winRate: 0.1454,
    completionRate: 0.625,
  },
  quality: {
    averageRating: 4.6,
    totalReviews: 18,
    complaintsCount: 1,
    supportTicketsCount: 1,
  },
  engagement: {
    conversationsCount: 22,
    responseRate: null,
    avgResponseTimeMinutes: null,
  },
  platform: {
    profileViews: 120,
    profileViewsLast30Days: 40,
    uniqueViewers: 27,
  },
  phases: {
    signup: { completed: true, status: 'APPROVED', otpVerified: true },
    profileCompletion: {
      completed: true,
      profileComplete: true,
      hasEmail: true,
      hasDescription: true,
      hasCertificates: true,
      hasRegions: true,
      hasYearsOfExperience: true,
      hasProfileImage: true,
      hasNationalId: true,
    },
    adminApproval: {
      completed: true,
      pendingDataRequests: 0,
      dataRequests: [
        {
          id: 100,
          status: 'APPROVED',
          message: 'Please upload clearer ID photo',
          requestedAt: '2026-03-15T10:00:00',
          submittedAt: '2026-03-16T09:30:00',
        },
      ],
    },
    onboarding: {
      completed: false,
      services: {
        added: true,
        count: 2,
        list: [
          { id: 1, nameAr: 'سباكة', nameEn: 'Plumbing' },
          { id: 5, nameAr: 'كهرباء', nameEn: 'Electrical' },
        ],
      },
      availability: {
        set: true,
        type: 'FIXED_TIMES',
        slotsCount: 3,
        slots: [{ dayOfWeek: 'SUNDAY', startTime: '09:00', endTime: '17:00', isActive: true }],
      },
      subscription: {
        active: true,
        categoryNameAr: 'باقة أساسية',
        categoryNameEn: 'Basic plan',
        startDate: '2026-03-01T00:00:00',
        endDate: '2027-03-01T23:59:59',
      },
      smallTaskTypes: { subscribed: false, count: 0, list: [] },
    },
    portfolio: { created: false, public: false, pastProjectsCount: 0 },
  },
});

export const MOCK_TECH_STATS: JourneyStatsResponse['stats'] = {
  totalTechnicians: 120,
  phaseCounts: {
    SIGNUP: 3,
    PROFILE: 8,
    APPROVAL: 12,
    ONBOARDING: 25,
    PORTFOLIO: 10,
    COMPLETE: 60,
    SUSPENDED: 2,
  },
  averageProgress: 62,
};

export const MOCK_CUSTOMER_JOURNEY: CustomerJourney = {
  customerId: 501,
  name: 'Customer Name (demo)',
  phoneNumber: '+966512345678',
  email: 'customer@example.com',
  status: 'APPROVED',
  registrationDate: '2026-02-20T09:10:00',
  acceptedTermsAt: '2026-02-20T09:10:00',
  lastLoginDate: '2026-04-01T18:00:00',
  lastProjectPostedDate: '2026-03-28T13:20:00',
  totalProjectsPosted: 6,
  projectsLast30Days: 2,
  projectsLast60Days: 4,
  quoteInteractions: {
    totalBidsReceived: 40,
    acceptedBids: 3,
    rejectedBids: 10,
    pendingBids: 27,
  },
};

export const MOCK_CUSTOMER_STATS: CustomerJourneyStatsResponse['stats'] = {
  totalCustomers: 890,
  averageProjectsLast30Days: 1,
};

async function getTechnicianJourney(technicianId: number): Promise<JourneyDetailResponse> {
  const { data } = await api.get<JourneyDetailResponse>(`/admin/technicians/${technicianId}/journey`);
  return data;
}

async function listTechnicianJourneys(phase?: string): Promise<JourneyListResponse> {
  const params = phase ? { phase } : undefined;
  const { data } = await api.get<JourneyListResponse>('/admin/technicians/journey', { params });
  return data;
}

async function getTechnicianJourneyStats(): Promise<JourneyStatsResponse> {
  const { data } = await api.get<JourneyStatsResponse>('/admin/technicians/journey/stats');
  return data;
}

async function getCustomerJourney(customerId: number): Promise<CustomerJourneyDetailResponse> {
  const { data } = await api.get<CustomerJourneyDetailResponse>(`/admin/customers/${customerId}/journey`);
  return data;
}

async function listCustomerJourneys(): Promise<CustomerJourneyListResponse> {
  const { data } = await api.get<CustomerJourneyListResponse>('/admin/customers/journey');
  return data;
}

async function getCustomerJourneyStats(): Promise<CustomerJourneyStatsResponse> {
  const { data } = await api.get<CustomerJourneyStatsResponse>('/admin/customers/journey/stats');
  return data;
}

export const journeyApi = {
  getTechnicianJourney,
  listTechnicianJourneys,
  getTechnicianJourneyStats,
  getCustomerJourney,
  listCustomerJourneys,
  getCustomerJourneyStats,

  async getTechnicianJourneyOrMock(technicianId: number): Promise<{ journey: TechnicianJourney; fromMock: boolean }> {
    try {
      const res = await getTechnicianJourney(technicianId);
      if (res.success && res.journey) {
        return { journey: normalizeTechnicianJourney(res.journey), fromMock: false };
      }
    } catch {
      /* mock */
    }
    return { journey: { ...MOCK_TECH_JOURNEY, technicianId }, fromMock: true };
  },

  async listTechniciansOrMock(phase?: string): Promise<{ journeys: TechnicianJourney[]; fromMock: boolean }> {
    try {
      const res = await listTechnicianJourneys(phase);
      if (res.success && Array.isArray(res.journeys)) {
        return { journeys: res.journeys.map(normalizeTechnicianJourney), fromMock: false };
      }
    } catch {
      /* fallthrough */
    }
    return { journeys: [MOCK_TECH_JOURNEY], fromMock: true };
  },

  async technicianStatsOrMock(): Promise<{ stats: JourneyStatsResponse['stats']; fromMock: boolean }> {
    try {
      const res = await getTechnicianJourneyStats();
      if (res.success && res.stats) return { stats: res.stats, fromMock: false };
    } catch {
      /* mock */
    }
    return { stats: MOCK_TECH_STATS, fromMock: true };
  },

  async getCustomerJourneyOrMock(customerId: number): Promise<{ journey: CustomerJourney; fromMock: boolean }> {
    try {
      const res = await getCustomerJourney(customerId);
      if (res.success && res.journey) return { journey: res.journey, fromMock: false };
    } catch {
      /* mock */
    }
    return { journey: { ...MOCK_CUSTOMER_JOURNEY, customerId }, fromMock: true };
  },

  async listCustomersOrMock(): Promise<{ journeys: CustomerJourney[]; fromMock: boolean }> {
    try {
      const res = await listCustomerJourneys();
      if (res.success && Array.isArray(res.journeys)) return { journeys: res.journeys, fromMock: false };
    } catch {
      /* fallthrough */
    }
    return { journeys: [MOCK_CUSTOMER_JOURNEY], fromMock: true };
  },

  async customerStatsOrMock(): Promise<{ stats: CustomerJourneyStatsResponse['stats']; fromMock: boolean }> {
    try {
      const res = await getCustomerJourneyStats();
      if (res.success && res.stats) return { stats: res.stats, fromMock: false };
    } catch {
      /* mock */
    }
    return { stats: MOCK_CUSTOMER_STATS, fromMock: true };
  },
};
